class MegaMenu extends HTMLElement {
    constructor() {
        super();
        this.timer = null;
        this.closeTimer = null;
        this.items = this.querySelectorAll('.header-menu.mega');
        this.menus = this.querySelectorAll('.header-menu-content');
        this.links = this.querySelectorAll('[data-menu]');
        this.currentFocusedElement = null;
        this.isKeyboardNavigation = false;

        // Initialize event listeners
        this.initMouseEvents();
        this.initKeyboardEvents();
        this.initFocusEvents();
        
        // Set initial ARIA states
        this.initAriaStates();
    }

    initAriaStates() {
        this.items.forEach((item) => {
            const trigger = item.querySelector('a[aria-haspopup="true"]');
            if (trigger) {
                trigger.setAttribute('aria-expanded', 'false');
            }
        });
    }

    initMouseEvents() {
        this.items.forEach((item) => {
            item.addEventListener('mouseenter', this.menuOpen.bind(this));
            item.addEventListener('mouseleave', this.menuClose.bind(this));
        });
        this.menus.forEach((menu) => {
            menu.addEventListener('mouseenter', this.menuEnter.bind(this));
            menu.addEventListener('mouseleave', this.menuClose.bind(this));
        });
        this.links.forEach((link) => {
            link.addEventListener('mouseenter', this.updateProducts.bind(this));
            link.addEventListener('keydown', this.updateProducts.bind(this));
        });
    }

    initKeyboardEvents() {
        // Main menu items keyboard navigation
        this.items.forEach((item) => {
            const trigger = item.querySelector('a[aria-haspopup="true"]');
            if (trigger) {
                trigger.addEventListener('keydown', this.handleMenuKeydown.bind(this));
                trigger.addEventListener('click', this.handleMenuClick.bind(this));
            }
        });

        // Submenu items keyboard navigation
        this.menus.forEach((menu) => {
            menu.addEventListener('keydown', this.handleSubmenuKeydown.bind(this));
        });

        // Global escape key handler
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                this.closeAllMenus();
            }
        });
    }

    initFocusEvents() {
        // Track focus method (keyboard vs mouse)
        document.addEventListener('mousedown', () => {
            this.isKeyboardNavigation = false;
        });

        document.addEventListener('keydown', (e) => {
            if (e.key === 'Tab') {
                this.isKeyboardNavigation = true;
            }
        });

        // Handle focus leaving the mega menu
        this.addEventListener('focusout', (e) => {
            // Check if focus is still within the mega menu
            setTimeout(() => {
                if (!this.contains(document.activeElement)) {
                    this.closeAllMenus();
                }
            }, 0);
        });
    }

    handleMenuKeydown(event) {
        const trigger = event.currentTarget;
        const item = trigger.closest('.header-menu');
        const isExpanded = trigger.getAttribute('aria-expanded') === 'true';

        switch (event.key) {
            case 'Enter':
            case ' ':
                event.preventDefault();
                if (isExpanded) {
                    this.closeMenu(item);
                } else {
                    this.openMenu(item, true);
                }
                break;
            case 'ArrowDown':
                event.preventDefault();
                if (!isExpanded) {
                    this.openMenu(item, true);
                } else {
                    this.focusFirstSubmenuItem(item);
                }
                break;
            case 'ArrowUp':
                event.preventDefault();
                if (isExpanded) {
                    this.closeMenu(item);
                }
                break;
            case 'ArrowRight':
                event.preventDefault();
                this.focusNextMenuItem(trigger);
                break;
            case 'ArrowLeft':
                event.preventDefault();
                this.focusPreviousMenuItem(trigger);
                break;
        }
    }

    handleMenuClick(event) {
        if (this.isKeyboardNavigation) {
            event.preventDefault();
            const trigger = event.currentTarget;
            const item = trigger.closest('.header-menu');
            const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
            
            if (isExpanded) {
                this.closeMenu(item);
            } else {
                this.openMenu(item, true);
            }
        }
    }

    handleSubmenuKeydown(event) {
        const menu = event.currentTarget;
        const focusableElements = this.getFocusableElements(menu);
        const currentIndex = focusableElements.indexOf(document.activeElement);

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                this.focusElement(focusableElements[currentIndex + 1] || focusableElements[0]);
                break;
            case 'ArrowUp':
                event.preventDefault();
                if (currentIndex === 0) {
                    // Focus back to parent menu item
                    const parentItem = menu.closest('.header-menu');
                    const trigger = parentItem.querySelector('a[aria-haspopup="true"]');
                    trigger.focus();
                } else {
                    this.focusElement(focusableElements[currentIndex - 1] || focusableElements[focusableElements.length - 1]);
                }
                break;
            case 'ArrowRight':
                event.preventDefault();
                // Handle navigation between product columns
                this.navigateProductColumns(event.target, 'next');
                break;
            case 'ArrowLeft':
                event.preventDefault();
                // Handle navigation between product columns
                this.navigateProductColumns(event.target, 'previous');
                break;
            case 'Tab':
                // Allow default tab behavior but close menu when tabbing out
                setTimeout(() => {
                    if (!menu.contains(document.activeElement)) {
                        this.closeAllMenus();
                    }
                }, 0);
                break;
            case 'Home':
                event.preventDefault();
                this.focusElement(focusableElements[0]);
                break;
            case 'End':
                event.preventDefault();
                this.focusElement(focusableElements[focusableElements.length - 1]);
                break;
        }
    }

    navigateProductColumns(currentElement, direction) {
        const currentProduct = currentElement.closest('.header-menu-product');
        if (!currentProduct) return;

        const allProducts = Array.from(currentProduct.parentElement.querySelectorAll('.header-menu-product'));
        const currentIndex = allProducts.indexOf(currentProduct);
        
        let targetIndex;
        if (direction === 'next') {
            targetIndex = currentIndex + 1 < allProducts.length ? currentIndex + 1 : 0;
        } else {
            targetIndex = currentIndex - 1 >= 0 ? currentIndex - 1 : allProducts.length - 1;
        }

        const targetLink = allProducts[targetIndex].querySelector('a');
        if (targetLink) {
            targetLink.focus();
        }
    }

    focusNextMenuItem(currentTrigger) {
        const allTriggers = Array.from(this.querySelectorAll('.header-menu > a'));
        const currentIndex = allTriggers.indexOf(currentTrigger);
        const nextIndex = (currentIndex + 1) % allTriggers.length;
        allTriggers[nextIndex].focus();
    }

    focusPreviousMenuItem(currentTrigger) {
        const allTriggers = Array.from(this.querySelectorAll('.header-menu > a'));
        const currentIndex = allTriggers.indexOf(currentTrigger);
        const previousIndex = currentIndex - 1 < 0 ? allTriggers.length - 1 : currentIndex - 1;
        allTriggers[previousIndex].focus();
    }

    focusFirstSubmenuItem(item) {
        const menu = item.querySelector('.header-menu-content');
        if (menu) {
            const firstFocusable = this.getFocusableElements(menu)[0];
            if (firstFocusable) {
                firstFocusable.focus();
            }
        }
    }

    getFocusableElements(container) {
        const focusableSelectors = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
        return Array.from(container.querySelectorAll(focusableSelectors));
    }

    focusElement(element) {
        if (element) {
            element.focus();
            // Update visual indicators for product selection
            if (element.hasAttribute('data-menu')) {
                this.updateProducts({ target: element });
            }
        }
    }

    openMenu(item, viaKeyboard = false) {
        // Close other menus first
        this.items.forEach((otherItem) => {
            if (otherItem !== item) {
                this.closeMenu(otherItem);
            }
        });

        clearTimeout(this.timer);
        clearTimeout(this.closeTimer);
        
        item.classList.add('active');
        document.querySelector('body').classList.add('desktop-menu-open');
        document.querySelector('body').classList.remove('desktop-menu-closing');
        
        // Update ARIA state
        const trigger = item.querySelector('a[aria-haspopup="true"]');
        if (trigger) {
            trigger.setAttribute('aria-expanded', 'true');
        }

        // Announce to screen readers
        if (viaKeyboard) {
            this.announceToScreenReader('Menu expanded');
        }
    }

    closeMenu(item) {
        item.classList.remove('active');
        
        // Update ARIA state
        const trigger = item.querySelector('a[aria-haspopup="true"]');
        if (trigger) {
            trigger.setAttribute('aria-expanded', 'false');
        }
    }

    closeAllMenus() {
        this.items.forEach((item) => {
            this.closeMenu(item);
        });
        document.querySelector('body').classList.remove('desktop-menu-open');
        document.querySelector('body').classList.remove('desktop-menu-closing');
    }

    menuClose(event) {
        // Don't close on keyboard navigation
        if (this.isKeyboardNavigation) return;
        
        let $self = this;
        this.timer = setTimeout(function(){
            document.querySelector('body').classList.remove('desktop-menu-open');
            document.querySelector('body').classList.add('desktop-menu-closing');
            $self.items.forEach((item) => {
                $self.closeMenu(item);
            });
            $self.closeTimer = setTimeout(function(){
                document.querySelector('body').classList.remove('desktop-menu-closing');
            }, 400);
        }, 200);
    }

    menuEnter(event) {
        clearTimeout(this.timer);
        clearTimeout(this.closeTimer);
        document.querySelector('body').classList.remove('desktop-menu-closing');
    }

    menuOpen(event) {
        // Don't handle during keyboard navigation
        if (this.isKeyboardNavigation) return;
        
        var clickedItem = event.currentTarget;
        this.openMenu(clickedItem);
    }

    updateProducts(event) {
        console.log(event.target);
        
        var clickedLink = event.target;
        var clickedLinkLevel = event.target.getAttribute('data-menu');
        if(clickedLinkLevel == null) {
            clickedLinkLevel = event.target.closest('.header-menu-link')?.getAttribute('data-menu');
        }
        if (event.type == 'keydown' && event.key == 'ArrowRight') {
            const productElement = this.querySelector(`[data-related-menu="${clickedLinkLevel}"] .header-menu-product a`);
            if (productElement) {
                productElement.focus();
                console.log('Focused element:', productElement);
            }
        }
        // Remove active class from all links
        this.links.forEach((link) => {
            link.classList.remove('active');
        });
        
        // Add active class to clicked link
        clickedLink.classList.add('active');

        // Update product displays
        this.relatedProducts = this.querySelectorAll('[data-related-menu]');
        this.relatedProducts.forEach((relatedProduct) => {
            if(relatedProduct.getAttribute('data-related-menu') != clickedLinkLevel) {
                relatedProduct.classList.add('hidden');
            } else {
                relatedProduct.classList.remove('hidden');
            }
        });

        // Announce change to screen readers
        if (this.isKeyboardNavigation) {
            this.announceToScreenReader(`${clickedLinkLevel} products now displayed`);
        }
    }

    announceToScreenReader(message) {
        // Create or update live region for announcements
        let liveRegion = document.getElementById('mega-menu-live-region');
        if (!liveRegion) {
            liveRegion = document.createElement('div');
            liveRegion.id = 'mega-menu-live-region';
            liveRegion.setAttribute('aria-live', 'polite');
            liveRegion.setAttribute('aria-atomic', 'true');
            liveRegion.className = 'visually-hidden';
            document.body.appendChild(liveRegion);
        }
        liveRegion.textContent = message;
        
        // Clear after announcement
        setTimeout(() => {
            liveRegion.textContent = '';
        }, 1000);
    }
}

customElements.define('mega-menu', MegaMenu);